import React, { ReactNode, useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { Configure, Hits, InfiniteHits, Pagination, useHits, usePagination } from 'react-instantsearch-hooks-web';
import { Category } from 'shared/types/product/Category';
import { mapProduct } from 'helpers/algolia/map-product';
import { useFormat } from 'helpers/hooks/useFormat';
import DesktopFacets from './components/desktop-facets';
import { FacetConfiguration } from './types';
import ProductTile from '../product-tile';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet } from 'helpers/utils/screensizes';
import { ACACIA, CARE, CIRRUS, CLEANFLUSH, CONTURA, COSMO, EASYSWITCH, ELEGANCE, HOMECARE, LIANO, LUNA, OPAL, SAHARA, URBANE, URBANEUNIVERSALACCESSORIES } from 'helpers/constants/collection-names';
import { localeMapping } from 'helpers/constants/localeMapping';
// import { CARE_COLLECTION } from 'helpers/constants/category';
interface Props {
  slug?: string;
  searchQuery?: string;
  categories: Category[];
  facetsConfiguration: Record<string, FacetConfiguration>;
}
interface NoResultProps {
  fallback: ReactNode;
}

function NoResultsBoundary({ children, fallback }: React.PropsWithChildren<NoResultProps>) {
  const { results } = useHits();

  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned.
  if (results && !results?.__isArtificial && results?.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

function NoResults() {
  // const { indexUiState } = useInstantSearch();

  return (
    <div>
      <p className="mt-20 text-center text-18 font-normal text-[#333]  lg:mt-0 2xl:text-22">No products found</p>
    </div>
  );
}
const CollectionProductListAlgolia: React.FC<Props> = ({ slug, searchQuery, categories, facetsConfiguration }) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { locale } = useParams();
  const localeKey = localeMapping[locale];
  const { refine } = usePagination();
  const { hits, results} = useHits();
  const isDisabled = document.body.classList.contains('disabled');
  const hitsLength = hits.length;
  const totalHits = results ? results.nbHits : 0;
  useEffect(() => {
    refine(0);
  }, [searchQuery]);
 

  //New collection page search query (Urbane universal accessories)
  const getSearchQuery = () => {
    if (slug === URBANEUNIVERSALACCESSORIES) 
    {
      return URBANEUNIVERSALACCESSORIES;
    }  
    else
    {
      return "";      
    }
  };

  //slug === CONTURA query updated. Showing only Contura II products. We remove Contura Products from the query.
  const getFilterQuery = () => {
    if (slug === URBANEUNIVERSALACCESSORIES) return "";
    if (slug === HOMECARE) return `variants.searchableAttributes.exclusive-product-AU:"BUNNINGS" AND searchableAttributes.sub-brand:"LiveWell"`;  //This is only for Australia.
    if (slug === COSMO) return `searchableAttributes.range:"Cosmo" OR searchableAttributes.range:"Cosmo Care"`;  
    if (slug === CLEANFLUSH) return `variants.attributes.clean-flush:true`;  
    if (slug === EASYSWITCH) return `(NOT variants.searchableAttributes.item-code:"849065C" AND NOT variants.searchableAttributes.item-code:"849061C" AND NOT variants.searchableAttributes.item-code:"98611C" AND NOT variants.searchableAttributes.item-code:"96378C") AND categoryKeys.${localeKey}:"Tapware" AND categoryKeys.${localeKey}:"Bath Shower Mixers" AND searchableAttributes.range:"Urbane II" OR searchableAttributes.range:"Contura II" OR searchableAttributes.range:"Liano II" OR searchableAttributes.range:"Opal" OR searchableAttributes.gwa-product-type:"Wall Mounted Tapware"`;  
    if (slug === ELEGANCE) return `searchableAttributes.range:"Elegance" OR searchableAttributes.range:"Elegance II"`;  
    if (slug === CIRRUS) return `searchableAttributes.range:"Cirrus"`;  
    if (slug === SAHARA) return `searchableAttributes.range:"Sahara"`;  
    if (slug === ACACIA) return `searchableAttributes.range:"Acacia"`;  
    if (slug === LUNA) return `searchableAttributes.range:"Luna"`;
    if (slug === OPAL) return ` searchableAttributes.range:"OPALSUPPORT" OR searchableAttributes.range:"Opal" OR searchableAttributes.range:"Opal II" OR searchableAttributes.range:"OpalCare"`
    if (slug === CONTURA) return `searchableAttributes.range:"Contura II"`
    if (slug === URBANE) return `searchableAttributes.range:"Urbane II" OR searchableAttributes.range:"Urbane"`
    if (slug === LIANO) return ` searchableAttributes.range:"Liano" OR searchableAttributes.range:"Liano Care" OR searchableAttributes.range:"Liano II" OR searchableAttributes.range:"Liano Nexus" OR searchableAttributes.range:"Liano Junior" OR searchableAttributes.range:"LIANOE"`
    if (slug === CARE) return `categoryKeys.${localeKey}:"Care Sinks" OR categoryKeys.${localeKey}:"Care Tapware" OR categoryKeys.${localeKey}:"Care Showers" OR categoryKeys.${localeKey}:"Care Toilets" OR categoryKeys.${localeKey}:"Care Accessories" OR categoryKeys.${localeKey}:"Care Basins" AND NOT searchableAttributes.range:opal AND NOT searchableAttributes.range:"Opal II Easy Height"`;
    return `searchableAttributes.range: "${slug} II"`;
  };

  return (
    <>
      <div id="plp-container" className="bg-[#F8F8F8]">
        <Configure
          query={getSearchQuery()}
          filters={getFilterQuery()}
          hitsPerPage={12}
          facets={['*']}
          attributesToHighlight={[]} // Disable highlighting
          attributesToSnippet={[]} // Disable snippetting
          attributesToRetrieve={[
            "name",
            "slug",
            "categoryKeys",
            "masterVariantID",
            "searchableAttributes.IsNew",
            "variants.id",
            "variants.key",
            "variants.sku",
            "variants.images",
            "variants.attributes.colour",
            "variants.attributes.item-code",
            "variants.attributes.item-name",
          ]}          
          maxValuesPerFacet={999}
        />
        <div className="flex flex-col items-end">
          <h1 className="w-full px-24 pt-48 text-center text-38  font-medium leading-[3rem] text-[#090909] lg:pb-8 2xl:pb-42 2xl:pt-80 2xl:text-48">
            Products in the <span className="capitalize">{slug?.replaceAll('-', ' ')}</span> Collection
          </h1>

          <div className="mt-8 flex w-full items-center justify-start gap-48 px-24 py-32 md:justify-center 2xl:px-80 2xl:py-24">
            <DesktopFacets facetsConfiguration={facetsConfiguration} />
          </div>
        </div>
        {/* // <div className="flex flex-col lg:col-span-9 lg:col-start-4"> */}

        <div className="items-start gap-24 px-24 pb-48 md:flex md:flex-row 2xl:gap-48 md:px-28 lg:px-48 xl:px-60 2xl:px-80">
          <div className="w-full flex-1  bg-[#F8F8F8] lg:mt-40">
            <NoResultsBoundary fallback={<NoResults />}>
              <InfiniteHits
                showPrevious={false}
                hitComponent={({ hit }: any) => (
                  <ProductTile images={hit?.images} product={mapProduct(hit, locale)} isCollectionPage={true} />
                )}
                classNames={{
                  root: 'pt-24 lg:pt-0',
                  list: 'grid md:grid-cols-3 lg:grid-cols-4 gap-16 ',
                  // loadMore: hits.length < 12
                  //   ? 'hidden'
                  //   : 'mt-48 border-1 rounded-none border border-[#2B2C39] bg-[#2B2C39] shadow-sm text-16 text-white hover:!bg-[#394A54] px-18 py-10 md:px-32 md:py-18 w-full md:w-full lg:w-200 lg:block lg:m-auto lg:mt-48 ',
                  loadMore:  totalHits > 12 && hitsLength >= 12 ? 'mt-48 border-1 rounded-none border border-[#2B2C39] bg-[#2B2C39] shadow-sm text-16 text-white hover:!bg-[#394A54] px-18 py-10 md:px-32 md:py-18 w-full md:w-full lg:w-200 lg:block lg:m-auto lg:mt-48' : '!hidden',
                }}
                translations={{
                  showMoreButtonText: formatProductMessage({ id: 'show.more', defaultMessage: 'Show More' }),
                }}
              />
            </NoResultsBoundary>
          </div>
        </div>
      </div>
    </>
  );
};
export default CollectionProductListAlgolia;
